/**
 * Procurement surveillance: the criticality rating of an equipment line (wave INST
 * ruling 11 gives every rating its line), and the notice lead time of a verification type
 * (wave INST ruling 3), the two configuration acts this wave's frontend carries.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * The total and the level are the server's, computed from the tenant's scheme at the
 * moment of rating; the client sends one whole score per element and never a total.
 */

import { parseCitations, type Citation } from "./standards";
import { apiJson, listBody, num, optStr, query, str } from "./client";

/** One element of the tenant's criticality scheme, as the taxonomies read model lists it. */
export interface CriticalityElement {
  readonly code: string;
  readonly label: string;
  readonly weight: number;
  /** Every element is scored 1 to this. */
  readonly scaleMax: number;
}

export interface EquipmentTypeOption {
  readonly id: string;
  readonly code: string;
  readonly label: string;
}

/** One verification type with both of its lead times. */
export interface VerificationTypeLead {
  readonly id: string;
  readonly code: string;
  readonly label: string;
  /** The contractor's RFI notice lead time in calendar days; null for none. */
  readonly noticeLeadDays: number | null;
  /** The operator's notice of a planned assessment to the contractor (the other way). */
  readonly notificationLeadDays: number | null;
  /** Wave STD ruling 12: the clauses this type cites. */
  readonly citations: readonly Citation[];
}

export interface RatingScheme {
  readonly elements: readonly CriticalityElement[];
  readonly equipmentTypes: readonly EquipmentTypeOption[];
  readonly verificationTypes: readonly VerificationTypeLead[];
}

function rows(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value)
    ? value.filter((one): one is Record<string, unknown> => typeof one === "object" && one !== null)
    : [];
}

function optNum(value: unknown): number | null {
  return typeof value === "number" ? value : null;
}

/** The scheme, the equipment types and the verification types, from GET /api/taxonomies. */
export async function readRatingScheme(token: string, signal?: AbortSignal): Promise<RatingScheme> {
  return apiJson(
    token,
    "/taxonomies",
    (raw) => {
      const body = typeof raw === "object" && raw !== null ? (raw as Record<string, unknown>) : {};
      return {
        elements: rows(body.criticality_elements)
          .sort((a, b) => num(a.sequence) - num(b.sequence))
          .map((one) => ({
            code: str(one.token),
            label: str(one.label),
            weight: num(one.weight),
            scaleMax: num(one.scale_max),
          })),
        equipmentTypes: rows(body.equipment_types)
          .filter((one) => typeof one.id === "string")
          .map((one) => ({ id: str(one.id), code: str(one.token), label: str(one.label) })),
        verificationTypes: rows(body.verification_types)
          .filter((one) => typeof one.id === "string")
          .map((one) => ({
            id: str(one.id),
            code: str(one.token),
            label: str(one.label),
            noticeLeadDays: optNum(one.notice_lead_days),
            notificationLeadDays: optNum(one.notification_lead_days),
            citations: parseCitations(one.citations),
          })),
      };
    },
    { signal },
  );
}

/** One rating revision, as the register answers it (CriticalityRatingOut). */
export interface CriticalityRating {
  readonly id: string;
  readonly humanId: string;
  readonly revision: number;
  readonly isCurrent: boolean;
  /** Null on a rating written before wave INST: it named no line. */
  readonly lineReference: string | null;
  readonly equipmentTypeLabel: string;
  readonly total: number;
  readonly levelCode: string;
  readonly levelLabel: string;
  readonly ratedByName: string | null;
  readonly ratedAt: string;
  readonly note: string | null;
}

function rating(body: Record<string, unknown>): CriticalityRating {
  return {
    id: str(body.id),
    humanId: str(body.human_id),
    revision: num(body.revision),
    isCurrent: body.is_current === true,
    lineReference: optStr(body.line_reference),
    equipmentTypeLabel: str(body.equipment_type_label),
    total: num(body.total),
    levelCode: str(body.level_code),
    levelLabel: str(body.level_label),
    ratedByName: optStr(body.rated_by_name),
    ratedAt: str(body.rated_at),
    note: optStr(body.note),
  };
}

/** The current ratings of one contract's equipment lines. */
export async function listRatings(
  token: string,
  contractId: string,
  signal?: AbortSignal,
): Promise<{ total: number; items: CriticalityRating[] }> {
  const body = await apiJson(
    token,
    `/criticality-ratings${query({ contract_id: contractId })}`,
    listBody,
    { signal },
  );
  return {
    total: body.total,
    items: body.items.map(rating),
  };
}

/** The rating body on the wire (CriticalityRatingCreate): what a refusal names. */
export interface CriticalityRatingPayload {
  project_id: string;
  contract_id: string;
  equipment_type_id: string;
  /** One whole score per live element, keyed by element code. */
  scores: Record<string, number>;
  note: string | null;
  /** Required from wave INST: the requisition item or tag the rating is for. */
  line_reference: string;
}

export async function createRating(
  token: string,
  payload: CriticalityRatingPayload,
): Promise<CriticalityRating> {
  return apiJson(
    token,
    "/criticality-ratings",
    (raw) =>
      rating(typeof raw === "object" && raw !== null ? (raw as Record<string, unknown>) : {}),
    { method: "POST", body: payload },
  );
}

/**
 * Set or clear one verification type's RFI notice lead time (CONFIGURE_TAXONOMIES; 404
 * for anyone else, 409 configuration.unchanged when the value is the one stored).
 */
export async function setNoticeLeadDays(
  token: string,
  verificationTypeId: string,
  noticeLeadDays: number | null,
): Promise<{ noticeLeadDays: number | null }> {
  return apiJson(
    token,
    `/configuration/verification-types/${verificationTypeId}`,
    (raw) => {
      const body = typeof raw === "object" && raw !== null ? (raw as Record<string, unknown>) : {};
      return { noticeLeadDays: optNum(body.notice_lead_days) };
    },
    { method: "PATCH", body: { notice_lead_days: noticeLeadDays } },
  );
}
